// PortalRoute Component - Portal-Specific Access Guard
import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useAuth, usePermissions, useOrganization } from '../../shared/hooks';
import { LoadingSpinner } from '../../shared/components';
import { getDefaultRedirectForUser } from '../routes/commonRoutes';
import { PermissionRoute } from './PermissionRoute';

/**
 * PortalRoute component that enforces portal-specific access control
 * Checks user roles, permissions, and organization context
 *
 * @param {Object} props - Component props
 * @param {React.Component} props.children - Child components to render
 * @param {string} props.portal - Portal name (bureau, admin, client, super-admin)
 * @param {Array} props.requiredRoles - Required user roles for portal access
 * @param {Array} props.requiredPermissions - Required permissions for portal access
 * @param {boolean} props.requireOrganization - Whether organization context is required
 * @param {string} props.fallbackRedirect - Fallback redirect path
 * @returns {React.Component} Protected route or redirect
 */
export const PortalRoute = ({
  children,
  portal,
  requiredRoles = [],
  requiredPermissions = [],
  requireOrganization = false,
  fallbackRedirect = null,
}) => {
  const { user, isAuthenticated, isLoading: authLoading, roles } = useAuth();
  const { hasRole, hasAnyRole, hasPermission, hasAnyPermission, canAccessPortal } =
    usePermissions();
  const { hasOrganization, currentOrganization } = useOrganization();

  // Show loading spinner while checking authentication and permissions
  if (authLoading) {
    return (
      <div className="route-loading-container">
        <LoadingSpinner size="large" message="Verifying portal access..." />
      </div>
    );
  }

  // Must be authenticated
  if (!isAuthenticated) {
    // Redirect to the appropriate login page based on the portal
    const loginPath = portal === 'super-admin' ? '/super-admin/login' : '/login';
    return <Navigate to={loginPath} replace />;
  }

  // Check portal access
  const hasPortalAccess = canAccessPortal(portal);

  if (!hasPortalAccess) {
    const userWithRoles = {
      ...user,
      roles: roles || [],
      groups: user?.groups || [],
    };
    // Don't redirect to the same portal we're trying to access
    const defaultRedirect = fallbackRedirect || getDefaultRedirectForUser(userWithRoles);
    if (defaultRedirect === `/${portal}` || defaultRedirect.startsWith(`/${portal}/`)) {
      return <Navigate to="/unauthorized" replace />;
    }
    return <Navigate to={defaultRedirect} replace />;
  }

  // Check specific required roles
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole =
      requiredRoles.length === 1 ? hasRole(requiredRoles[0]) : hasAnyRole(requiredRoles);

    if (!hasRequiredRole) {
      const userWithRoles = {
        ...user,
        roles: roles || [],
        groups: user?.groups || [],
      };
      const defaultRedirect = fallbackRedirect || getDefaultRedirectForUser(userWithRoles);
      return <Navigate to={defaultRedirect} replace />;
    }
  }

  // Check specific required permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasRequiredPermissions = requiredPermissions.every((permission) =>
      hasPermission(permission)
    );

    if (!hasRequiredPermissions) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check organization context requirement
  if (requireOrganization && !hasOrganization) {
    return <Navigate to="/select-organization" replace />;
  }

  // Render protected portal content
  return (
    <Suspense
      fallback={
        <div className="route-loading-container">
          <LoadingSpinner size="medium" message="Loading portal..." />
        </div>
      }
    >
      {children}
    </Suspense>
  );
};

/**
 * Portal-specific route components for each portal type
 */
export const BureauPortalRoute = ({ children, ...props }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Remove hardcoded role requirements - let canAccessPortal handle it
  return (
    <PortalRoute
      portal="bureau"
      requiredRoles={[]} // Empty - portal access is determined by assigned_portal field
      requiredPermissions={[]} // Empty - permissions handled dynamically
      {...props}
    >
      {children}
    </PortalRoute>
  );
};

export const AdminPortalRoute = ({ children, ...props }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Remove hardcoded role requirements - let canAccessPortal handle it
  return (
    <PortalRoute
      portal="admin"
      requiredRoles={[]} // Empty - portal access is determined by assigned_portal field
      requiredPermissions={[]} // Empty - permissions handled dynamically
      {...props}
    >
      {children}
    </PortalRoute>
  );
};

export const ClientPortalRoute = ({ children, ...props }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Remove hardcoded role requirements - let canAccessPortal handle it
  return (
    <PortalRoute
      portal="client"
      requiredRoles={[]} // Empty - portal access is determined by assigned_portal field
      requiredPermissions={[]} // Empty - no specific permissions required for client portal
      {...props}
    >
      {children}
    </PortalRoute>
  );
};

export const SuperAdminPortalRoute = ({ children, ...props }) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/super-admin/login" replace />;
  }

  // Allow root admin users even without explicit role
  if (user?.is_root_admin || user?.is_superuser) {
    return children;
  }

  return (
    <PortalRoute
      portal="super-admin"
      requiredRoles={['Super Admin']}
      requiredPermissions={['super_admin_access']}
      {...props}
    >
      {children}
    </PortalRoute>
  );
};

export const TiplockPortalRoute = ({ children, ...props }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Portal access is determined by assigned_portal field or allowed_portals
  return (
    <PortalRoute
      portal="tiplock"
      requiredRoles={[]} // Empty - portal access is determined by assigned_portal field
      requiredPermissions={[]} // Empty - permissions handled dynamically
      {...props}
    >
      {children}
    </PortalRoute>
  );
};

export const SalesPortalRoute = ({ children, ...props }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Portal access is determined by assigned_portal field or allowed_portals
  return (
    <PortalRoute
      portal="sales"
      requiredRoles={[]} // Empty - portal access is determined by assigned_portal field
      requiredPermissions={[]} // Empty - permissions handled dynamically
      {...props}
    >
      {children}
    </PortalRoute>
  );
};

export const TechnicianPortalRoute = ({ children, ...props }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Portal access is determined by assigned_portal == 'technician'.
  return (
    <PortalRoute
      portal="technician"
      requiredRoles={[]} // Empty - portal access is determined by assigned_portal field
      requiredPermissions={[]} // Empty - permissions handled dynamically
      {...props}
    >
      {children}
    </PortalRoute>
  );
};

export const InsurancePortalRoute = ({ children, ...props }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin-only for now: canAccessPortal('insurance') admits admins and
  // super-admins, and refuses everyone else (see usePermissions).
  return (
    <PortalRoute portal="insurance" requiredRoles={[]} requiredPermissions={[]} {...props}>
      {children}
    </PortalRoute>
  );
};

InsurancePortalRoute.propTypes = {
  children: PropTypes.node,
};

/**
 * Portal layout wrapper that provides portal-specific context
 *
 * @param {Object} props - Component props
 * @param {React.Component} props.children - Child components
 * @param {string} props.portal - Portal name
 * @param {Object} props.layoutConfig - Portal-specific layout configuration
 * @returns {React.Component} Portal layout wrapper
 */
export const PortalLayoutWrapper = ({ children, portal, layoutConfig = {} }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();

  // Portal-specific context data
  const portalContext = {
    portal,
    user,
    organization: currentOrganization,
    layoutConfig,
  };

  return (
    <div
      className={`portal-layout portal-${portal}`}
      data-portal={portal}
      data-user-id={user?.id}
      data-organization-id={currentOrganization?.id}
    >
      {children}
    </div>
  );
};

/**
 * Multi-portal route that allows access from multiple portals
 * Useful for shared components that can be accessed from different portals
 *
 * @param {Object} props - Component props
 * @param {React.Component} props.children - Child components
 * @param {Array} props.allowedPortals - List of allowed portals
 * @param {Array} props.requiredPermissions - Required permissions
 * @returns {React.Component} Multi-portal protected route
 */
export const MultiPortalRoute = ({ children, allowedPortals = [], requiredPermissions = [] }) => {
  const { canAccessPortal } = usePermissions();

  // Check if user can access at least one of the allowed portals
  const hasPortalAccess =
    allowedPortals.length === 0 || allowedPortals.some((portal) => canAccessPortal(portal));

  if (!hasPortalAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Use PermissionRoute for additional permission checking
  return <PermissionRoute requiredPermissions={requiredPermissions}>{children}</PermissionRoute>;
};

export default PortalRoute;
